import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Star, Minus, Plus, ShoppingCart, Zap, Banknote, Truck, ShieldCheck, ChevronRight, Heart, BadgeCheck, Beaker, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useFavorites } from "../context/FavoritesContext";
import { useAuth } from "../context/AuthContext";
import { fmtPrice } from "../lib/shop";
import { ProductCard, AromaBadge } from "../components/ProductCard";

function Stars({ value, className = "h-4 w-4" }) {
  return (
    <span className="flex items-center gap-0.5 text-amber-400">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`${className} ${i < Math.round(value) ? "fill-amber-400" : "fill-line text-line"}`} />
      ))}
    </span>
  );
}

function ReviewsSection({ productId }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api
      .get(`/products/${productId}/reviews`)
      .then((r) => setData(r.data))
      .catch(() => setData({ reviews: [], average: null, count: 0, can_review: false }));
  }, [productId]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, comment });
      toast.success("Recenzia ta a fost publicată. Mulțumim!");
      setComment("");
      setRating(5);
      load();
    } catch (err) {
      toast.error(apiError(err, "Recenzia nu a putut fi trimisă"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-20" data-testid="reviews-section">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px]">Recenzii clienți</h2>
          {data && (
            <div className="mt-2 flex items-center gap-3" data-testid="reviews-summary">
              {data.count > 0 ? (
                <>
                  <span className="font-display text-4xl font-bold text-navy">{data.average}</span>
                  <div>
                    <Stars value={data.average} />
                    <p className="text-sm text-muted2">{data.count} {data.count === 1 ? "recenzie" : "recenzii"} verificate</p>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted2">Nicio recenzie încă. Fii primul care împărtășește experiența.</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 grid gap-10 lg:grid-cols-12">
        <div className="flex flex-col gap-4 lg:col-span-7" data-testid="reviews-list">
          {data?.reviews.map((r) => (
            <div key={r.id} className="rounded-lg border border-line bg-white p-6" data-testid={`review-item-${r.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-skytint font-display text-sm font-bold text-navy">
                    {(r.user_name || "C")[0].toUpperCase()}
                  </span>
                  <div>
                    <p className="text-sm font-bold text-navy">{r.user_name}</p>
                    <p className="flex items-center gap-1 text-xs text-muted2">
                      <BadgeCheck className="h-3.5 w-3.5 text-navy" /> Cumpărător verificat
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <Stars value={r.rating} />
                  <p className="mt-1 text-xs text-muted2">
                    {new Date(r.created_at).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" })}
                  </p>
                </div>
              </div>
              {r.comment && <p className="mt-4 text-sm leading-relaxed text-ink">{r.comment}</p>}
            </div>
          ))}
        </div>

        <div className="lg:col-span-5">
          {!user ? (
            <div className="rounded-lg border border-line bg-[#F4F6F8] p-7" data-testid="review-login-hint">
              <p className="font-display text-base font-bold text-navy">Ai cumpărat acest produs?</p>
              <p className="mt-2 text-sm text-muted2">
                <Link to="/autentificare" className="font-bold text-navy underline" data-testid="review-login-link">Autentifică-te</Link>{" "}
                pentru a lăsa o recenzie. Doar clienții care au cumpărat produsul pot scrie.
              </p>
            </div>
          ) : data?.can_review ? (
            <form onSubmit={submit} className="rounded-lg border border-line bg-[#F4F6F8] p-7" data-testid="review-form">
              <p className="font-display text-base font-bold text-navy">
                {data.has_reviewed ? "Actualizează recenzia ta" : "Lasă o recenzie"}
              </p>
              <label className="mt-5 block text-xs font-bold uppercase tracking-[0.08em] text-muted2">Nota ta</label>
              <div className="mt-2 flex gap-1" data-testid="review-rating-input">
                {[1, 2, 3, 4, 5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRating(s)}
                    className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:bg-skytint"
                    aria-label={`${s} stele`}
                    data-testid={`review-star-${s}`}
                  >
                    <Star className={`h-6 w-6 ${s <= rating ? "fill-amber-400 text-amber-400" : "fill-line text-line"}`} />
                  </button>
                ))}
              </div>
              <label className="mt-4 block text-xs font-bold uppercase tracking-[0.08em] text-muted2">Comentariu (opțional)</label>
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Cum ți s-a părut produsul?"
                className="mt-2 w-full resize-none rounded-lg border border-line bg-white px-4 py-3 text-sm outline-none transition-colors focus:border-sky2 focus:ring-2 focus:ring-sky2/20"
                data-testid="review-comment-input"
              />
              <button
                type="submit"
                disabled={sending}
                className="mt-5 flex h-12 w-full items-center justify-center rounded-lg bg-navy text-base font-bold text-white transition-colors hover:bg-navy-dark disabled:opacity-60"
                data-testid="review-submit-button"
              >
                {sending ? "Se trimite..." : "Publică recenzia"}
              </button>
            </form>
          ) : (
            <div className="rounded-lg border border-line bg-[#F4F6F8] p-7" data-testid="review-not-eligible">
              <p className="font-display text-base font-bold text-navy">Recenzii doar de la cumpărători</p>
              <p className="mt-2 text-sm text-muted2">
                Poți lăsa o recenzie după ce comanzi acest produs. Așa păstrăm recenziile reale și utile.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function parseVolume(name) {
  const m = name.match(/([\d,.]+)\s*L\b/i);
  if (!m) return null;
  return { num: parseFloat(m[1].replace(",", ".")), label: m[0].trim() };
}

function baseName(name) {
  return name.replace(/\s*[\d,.]+\s*L\b/gi, "").replace(/\s+/g, " ").trim();
}

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { isFav, toggle } = useFavorites();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [variants, setVariants] = useState([]);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    setProduct(null);
    setVariants([]);
    setQty(1);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        api
          .get("/products", { params: { category: r.data.category } })
          .then((rr) => setRelated(rr.data.filter((p) => p.id !== id).slice(0, 4)))
          .catch(() => {});
        api
          .get("/products")
          .then((all) => {
            const base = baseName(r.data.name);
            const vs = all.data
              .filter((p) => p.aroma === r.data.aroma && baseName(p.name) === base)
              .map((p) => ({ id: p.id, num: parseVolume(p.name)?.num ?? 0, label: parseVolume(p.name)?.label || "", price: p.price, stock: p.stock }))
              .sort((a, b) => a.num - b.num);
            setVariants(vs);
          })
          .catch(() => {});
      })
      .catch(() => setProduct(false));
  }, [id]);

  if (product === null) {
    return (
      <main className="mx-auto max-w-[1120px] px-5 py-20 sm:px-8" data-testid="product-loading">
        <div className="grid gap-12 lg:grid-cols-2">
          <div className="aspect-square animate-pulse rounded-lg bg-skytint" />
          <div className="flex flex-col gap-4">
            <div className="h-8 w-2/3 animate-pulse rounded bg-skytint" />
            <div className="h-6 w-1/3 animate-pulse rounded bg-skytint" />
            <div className="h-40 w-full animate-pulse rounded bg-skytint" />
          </div>
        </div>
      </main>
    );
  }

  if (product === false) {
    return (
      <main className="mx-auto flex max-w-[1120px] flex-col items-center gap-4 px-5 py-32 text-center" data-testid="product-not-found">
        <h1 className="font-display text-2xl font-bold text-navy">Produsul nu a fost găsit</h1>
        <Link to="/produse" className="flex h-12 items-center rounded-lg bg-navy px-8 text-base font-bold text-white hover:bg-navy-dark">
          Înapoi la produse
        </Link>
      </main>
    );
  }

  const outOfStock = product.stock <= 0;
  const fav = isFav(product.id);
  const washesMatch = (product.volume || "").match(/(\d+)\s*(?:de\s+)?spălări/i);
  const perWash = washesMatch ? product.price / parseInt(washesMatch[1], 10) : null;

  const buyNow = () => {
    addItem(product, qty, false);
    navigate("/checkout");
  };

  return (
    <main data-testid="product-page">
      <div className="mx-auto max-w-[1120px] px-5 py-10 sm:px-8">
        <nav className="mb-8 flex items-center gap-1.5 text-sm font-semibold text-muted2" data-testid="product-breadcrumb">
          <Link to="/" className="hover:text-navy">Acasă</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <Link to="/produse" className="hover:text-navy">Produse</Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-ink">{product.name}</span>
        </nav>

        <div className="grid gap-12 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="overflow-hidden rounded-lg border border-line bg-skytint">
              <img src={product.image} alt={product.name} className="aspect-square w-full object-cover" data-testid="product-image" />
            </div>
            <div className="absolute left-4 top-4 flex flex-col items-start gap-2">
              {product.aroma && <AromaBadge aroma={product.aroma} />}
              {product.tag && (
                <span className="rounded-full bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-navy border border-line">
                  {product.tag}
                </span>
              )}
            </div>
            <button
              onClick={() => toggle(product.id)}
              className={`absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-line bg-white transition-colors ${fav ? "text-rose-500" : "text-muted2 hover:text-rose-500"}`}
              aria-label={fav ? "Elimină de la favorite" : "Adaugă la favorite"}
              data-testid="favorite-toggle-button"
            >
              <Heart className={`h-5 w-5 ${fav ? "fill-rose-500" : ""}`} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            <span className="text-xs font-bold uppercase tracking-[0.08em] text-muted2">{product.category}</span>
            <h1 className="mt-3 font-display text-3xl font-bold leading-[1.15] tracking-tight text-navy sm:text-4xl" data-testid="product-name">
              {product.name}
            </h1>
            <div className="mt-3 flex items-center gap-2 text-sm text-muted2">
              <Stars value={product.rating} />
              <span className="font-bold text-ink">{product.rating}</span>
              <span>• {product.reviews_count} recenzii</span>
            </div>

            <div className="mt-6 flex items-end gap-3">
              <span className="font-display text-[28px] font-bold leading-none text-navy sm:text-3xl" data-testid="product-price">
                {fmtPrice(product.price)}
              </span>
              {product.old_price > product.price && (
                <span className="pb-0.5 text-base text-muted2 line-through">{fmtPrice(product.old_price)}</span>
              )}
            </div>
            <p className="mt-1.5 text-sm text-muted2">
              {product.volume}
              {perWash && <span className="font-semibold text-navy"> · ≈ {fmtPrice(perWash)} per spălare</span>}
            </p>
            {product.sku && <p className="mt-1 text-xs font-semibold uppercase tracking-[0.08em] text-muted2" data-testid="product-sku">Cod produs: {product.sku}</p>}
            <p className={`mt-1.5 text-sm font-bold ${outOfStock ? "text-rose-600" : "text-navy"}`} data-testid="product-stock">
              {outOfStock ? "Stoc epuizat" : `În stoc (${product.stock} buc.)`}
            </p>

            {variants.length > 1 && (
              <div className="mt-6" data-testid="volume-selector">
                <p className="text-xs font-bold uppercase tracking-[0.08em] text-muted2">Alege volumul</p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  {variants.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => v.id !== product.id && navigate(`/produs/${v.id}`)}
                      disabled={v.stock <= 0}
                      className={`flex min-w-[92px] flex-col items-center rounded-lg border px-4 py-2.5 transition-colors ${
                        v.id === product.id
                          ? "border-navy bg-navy text-white"
                          : v.stock <= 0
                            ? "border-line bg-white text-muted2 opacity-50"
                            : "border-line bg-white text-navy hover:border-sky2 hover:bg-skytint"
                      }`}
                      data-testid={`volume-option-${v.id}`}
                      aria-pressed={v.id === product.id}
                    >
                      <span className="text-sm font-bold">{v.label}</span>
                      <span className={`text-xs ${v.id === product.id ? "text-sky-light" : "text-muted2"}`}>{fmtPrice(v.price)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <p className="mt-6 max-w-xl leading-relaxed text-ink" data-testid="product-description">{product.description}</p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-3 rounded-lg border border-line bg-white px-3 py-2.5">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="flex h-8 w-8 items-center justify-center text-muted2 hover:text-navy" aria-label="Scade" data-testid="qty-minus-button">
                  <Minus className="h-4 w-4" />
                </button>
                <span className="min-w-6 text-center font-display text-lg font-bold text-navy" data-testid="qty-value">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="flex h-8 w-8 items-center justify-center text-muted2 hover:text-navy" aria-label="Crește" data-testid="qty-plus-button">
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={() => { addItem(product, qty); toast.success("Adăugat în coș", { description: product.name }); }}
                disabled={outOfStock}
                className="flex h-12 items-center gap-2 rounded-lg bg-navy px-7 text-base font-bold text-white transition-colors hover:bg-navy-dark disabled:cursor-not-allowed disabled:bg-line"
                data-testid="add-to-cart-button"
              >
                <ShoppingCart className="h-4 w-4" /> Adaugă în coș
              </button>
              <button
                onClick={buyNow}
                disabled={outOfStock}
                className="flex h-12 items-center gap-2 rounded-lg border-[1.5px] border-navy px-7 text-base font-bold text-navy transition-colors hover:bg-skytint disabled:cursor-not-allowed disabled:border-line disabled:text-muted2"
                data-testid="buy-now-button"
              >
                <Zap className="h-4 w-4" /> Comandă rapidă
              </button>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              {[
                { icon: Banknote, text: "Plată ramburs la livrare" },
                { icon: Truck, text: "Livrare în 24-48 de ore" },
                { icon: ShieldCheck, text: "Retur simplu în 14 zile" },
              ].map((b) => (
                <div key={b.text} className="flex items-center gap-2.5 rounded-lg border border-line bg-white px-4 py-3.5">
                  <b.icon className="h-5 w-5 shrink-0 text-navy" />
                  <span className="text-xs font-bold leading-snug text-ink">{b.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {(product.usage || product.composition) && (
          <div className="mt-16 grid gap-6 lg:grid-cols-2" data-testid="product-details-blocks">
            {product.usage && (
              <div className="rounded-2xl border border-sky2/30 bg-skytint/70 p-7 shadow-[0_1px_2px_rgba(27,75,122,0.06)]">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-navy shadow-sm">
                    <Beaker className="h-5 w-5" />
                  </span>
                  <h2 className="font-display text-xl font-bold text-navy">Mod de utilizare și dozare</h2>
                </div>
                <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-ink" data-testid="product-usage">{product.usage}</p>
              </div>
            )}
            {product.composition && (
              <div className="rounded-2xl border border-sky2/30 bg-skytint/70 p-7 shadow-[0_1px_2px_rgba(27,75,122,0.06)]">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-navy shadow-sm">
                    <FileText className="h-5 w-5" />
                  </span>
                  <h2 className="font-display text-xl font-bold text-navy">Compoziție</h2>
                </div>
                <p className="mt-4 whitespace-pre-line text-sm leading-relaxed text-ink" data-testid="product-composition">{product.composition}</p>
              </div>
            )}
          </div>
        )}

        <ReviewsSection productId={id} />

        {related.length > 0 && (
          <section className="mt-20" data-testid="related-products">
            <h2 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-[32px]">
              Produse similare
            </h2>
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </section>
        )}
      </div>
    </main>
  );
}
